import { PageHero } from "@/components/site/sections";
import { Reveal } from "@/components/site/ui";
import { IMAGES } from "@/data/content";

// India-compliant Terms & Conditions references:
//  • Indian Contract Act, 1872
//  • Information Technology Act, 2000 and IT (Intermediary Guidelines & Digital Media
//    Ethics Code) Rules, 2021 — grievance redressal obligations
//  • Consumer Protection Act, 2019 and CP (E-Commerce) Rules, 2020
//  • Sale of Goods Act, 1930

const EFFECTIVE_DATE = "9 September 2026";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-32 mt-14">
    <h2 className="font-head text-2xl sm:text-3xl font-medium tracking-tight text-[#1C1A17]">
      {title}
    </h2>
    <div className="mt-4 text-[#3F3B34] leading-[1.8] space-y-4">{children}</div>
  </section>
);

const TOC = [
  { id: "acceptance", label: "Acceptance of terms" },
  { id: "services", label: "Our services" },
  { id: "eligibility", label: "Eligibility & account" },
  { id: "quotes", label: "Quotes, orders & contract" },
  { id: "payments", label: "Payments & taxes" },
  { id: "cancellation", label: "Cancellation & refund" },
  { id: "warranty", label: "Warranty & workmanship" },
  { id: "ip", label: "Intellectual property" },
  { id: "user-conduct", label: "Acceptable use" },
  { id: "third-party", label: "Third-party services" },
  { id: "liability", label: "Limitation of liability" },
  { id: "force-majeure", label: "Force majeure" },
  { id: "governing-law", label: "Governing law & jurisdiction" },
  { id: "grievance", label: "Grievance Officer" },
  { id: "changes", label: "Changes to these terms" },
  { id: "contact", label: "Contact" },
];

export default function Terms() {
  return (
    <>
      <PageHero
        overline="Legal"
        title="Terms & Conditions"
        subtitle="The legal terms that govern your use of the Spherio Living website and services."
        image={IMAGES.interior1}
      />

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14">
        {/* Sticky TOC */}
        <aside className="lg:col-span-4 lg:sticky lg:top-32 h-max">
          <div className="rounded-2xl border border-black/10 p-6">
            <div className="text-xs tracking-[0.2em] uppercase text-[#5C574F] font-bold">
              On this page
            </div>
            <ul className="mt-4 space-y-2.5 text-sm">
              {TOC.map((t) => (
                <li key={t.id}>
                  <a
                    href={`#${t.id}`}
                    className="text-[#1C1A17] hover:text-[#B8935A] transition-colors"
                  >
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-6 border-t border-black/10 text-xs text-[#5C574F]">
              Effective: {EFFECTIVE_DATE}
              <br />
              Version 1.0
            </div>
          </div>
        </aside>

        {/* Body */}
        <div className="lg:col-span-8" data-testid="terms-content">
          <Reveal>
            <p className="text-lg leading-relaxed text-[#3F3B34]">
              These Terms & Conditions ("Terms") govern your access to and use of the
              website{" "}
              <a
                href="https://spherioliving.com"
                className="text-[#1C1A17] underline underline-offset-4"
              >
                spherioliving.com
              </a>{" "}
              (the "Website") and the design, manufacturing, installation, renovation
              and allied services ("Services") offered by <strong>Spherio Living
              Private Limited</strong>, a company incorporated under the Companies
              Act, 2013 with its registered office at SCO No. 222, Shopping Square 1, Sector D,
              Sushant Golf City, Lucknow 226030, Uttar Pradesh, India ("Spherio
              Living", "we", "us" or "our"). By using the Website or engaging our
              Services, you agree to be bound by these Terms.
            </p>
          </Reveal>

          <Section id="acceptance" title="1. Acceptance of terms">
            <p>
              These Terms constitute a legally binding electronic record between you
              and Spherio Living, generated by a computer system and published in
              accordance with the Information Technology Act, 2000 and the rules made
              thereunder. No physical or digital signature is required. If you do not
              agree to any part of these Terms, please do not use the Website or engage
              our Services.
            </p>
          </Section>

          <Section id="services" title="2. Our services">
            <p>
              Spherio Living offers premium modular kitchens, wardrobes, complete home
              interiors, architectural design, renovation and smart-home solutions. All
              designs, drawings, quotes and delivery timelines shared through the
              Website or by our design team are indicative and become binding only on
              execution of a signed order confirmation or work agreement.
            </p>
          </Section>

          <Section id="eligibility" title="3. Eligibility & account">
            <p>
              You must be at least 18 years of age and capable of entering into a
              legally binding contract under the Indian Contract Act, 1872 to use the
              Website or engage our Services. If you create an account with us, you
              are responsible for maintaining the confidentiality of your credentials
              and for all activity that occurs under your account. Please notify us
              immediately of any unauthorised use.
            </p>
          </Section>

          <Section id="quotes" title="4. Quotes, orders & contract">
            <p>
              Any quote, estimate or "Instant Quote" generated on the Website is an
              indicative price only, based on the inputs you provide. It does not
              constitute an offer capable of acceptance. A binding contract for our
              Services is formed only when (a) you and Spherio Living sign a written
              work agreement or purchase order, and (b) any advance payment specified
              in that agreement is received by us.
            </p>
            <p>
              We reserve the right to revise a quote in the event of design changes,
              site conditions discovered during survey, material price fluctuations,
              or additional scope requested by you — subject to your prior approval
              in writing.
            </p>
          </Section>

          <Section id="payments" title="5. Payments & taxes">
            <p>
              Payment schedules will be set out in your work agreement. All prices are
              exclusive of GST and other applicable statutory taxes, which will be
              charged additionally at the prevailing rates. Payments must be made
              through the payment channels specified by Spherio Living (bank transfer,
              UPI or authorised payment aggregator). Any transaction fees or bank
              charges are to be borne by you unless expressly agreed otherwise.
            </p>
            <p>
              Payments once made are non-refundable except as stated in the
              "Cancellation & refund" clause below or as required by applicable law.
            </p>
          </Section>

          <Section id="cancellation" title="6. Cancellation & refund">
            <p>
              You may cancel a project at any stage by giving us written notice. The
              refund amount will be calculated as follows:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Before design sign-off:</strong> refund of the advance paid
                less design/consultation fees actually incurred.
              </li>
              <li>
                <strong>After design sign-off but before manufacturing begins:</strong>{" "}
                refund of amounts paid less design fees and 10% of the order value
                towards project management overheads.
              </li>
              <li>
                <strong>After manufacturing begins:</strong> no refund of amounts
                paid; any balance for materials procured or manufactured on your
                specification remains payable.
              </li>
            </ul>
            <p>
              Approved refunds will be processed within 7 business days of receipt of
              your written cancellation and any information required (such as bank
              details).
            </p>
          </Section>

          <Section id="warranty" title="7. Warranty & workmanship">
            <p>
              Spherio Living stands behind its workmanship. Modular products carry the
              manufacturer's warranty for the applicable hardware and finishes as
              specified in your order confirmation. Civil and installation work is
              warranted against manufacturing or workmanship defects for the period
              stated in your work agreement.
            </p>
            <p>
              The warranty does not cover: (i) damage caused by misuse, negligence,
              accidents, unauthorised modifications or repairs; (ii) normal wear and
              tear; (iii) damage caused by acts of God, water seepage, structural
              defects of the premises or third-party works; and (iv) products or
              materials supplied directly by you.
            </p>
          </Section>

          <Section id="ip" title="8. Intellectual property">
            <p>
              All content on the Website — including designs, 3D renders, floor plans,
              copy, images, logos, videos and software — is the intellectual property
              of Spherio Living or its licensors and is protected under the Copyright
              Act, 1957 and the Trade Marks Act, 1999. You may not copy, reproduce,
              republish, distribute, modify or create derivative works from any part
              of the Website without our prior written permission.
            </p>
            <p>
              Custom designs and drawings prepared for you remain the intellectual
              property of Spherio Living. You are granted a non-exclusive,
              non-transferable licence to use them for your own residence. You may not
              share or use them for any commercial purpose.
            </p>
          </Section>

          <Section id="user-conduct" title="9. Acceptable use">
            <p>You agree not to use the Website to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Post or transmit any unlawful, defamatory, obscene, harassing or infringing content.</li>
              <li>Attempt to gain unauthorised access to our systems, other users' accounts, or any linked service.</li>
              <li>Introduce viruses, malware or any code that may disrupt the Website.</li>
              <li>Impersonate any person or falsely state your affiliation with any person.</li>
              <li>Use automated means (scrapers, bots) to harvest data or content from the Website without our prior written consent.</li>
            </ul>
            <p>
              Violation may result in immediate termination of your account and, where
              appropriate, referral to law-enforcement authorities under the IT Act
              and other applicable laws.
            </p>
          </Section>

          <Section id="third-party" title="10. Third-party services">
            <p>
              The Website integrates with certain third-party services (such as Google
              Sign-In, email delivery, payment aggregators and mapping services). Your
              use of those services is subject to their respective terms and privacy
              policies, and Spherio Living is not responsible for their availability,
              accuracy or conduct.
            </p>
          </Section>

          <Section id="liability" title="11. Limitation of liability">
            <p>
              To the maximum extent permitted by applicable law, Spherio Living, its
              directors, employees and affiliates shall not be liable for any
              indirect, incidental, special, consequential or punitive damages
              (including loss of profits, revenue, goodwill or data) arising out of or
              in connection with your use of the Website or our Services. Our total
              aggregate liability for any claim arising in connection with a Service
              engagement shall not exceed the total amount paid by you to Spherio
              Living for that specific engagement in the twelve (12) months preceding
              the claim.
            </p>
            <p>
              Nothing in these Terms excludes or limits any liability that cannot be
              excluded or limited under the Consumer Protection Act, 2019 or other
              applicable Indian law.
            </p>
          </Section>

          <Section id="force-majeure" title="12. Force majeure">
            <p>
              Spherio Living shall not be liable for any failure or delay in
              performance that is caused by circumstances beyond its reasonable
              control, including acts of God, natural disasters, pandemics, war,
              terrorism, government action, strikes, lockouts, disruption of
              transport, or failure of utilities or telecommunications.
            </p>
          </Section>

          <Section id="governing-law" title="13. Governing law & jurisdiction">
            <p>
              These Terms are governed by and construed in accordance with the laws of
              the Republic of India. Any dispute arising out of or in connection with
              these Terms or the Services shall first be attempted to be resolved
              amicably by mutual discussion within 30 days. Failing which, the dispute
              shall be subject to the exclusive jurisdiction of the courts of Lucknow,
              Uttar Pradesh, India.
            </p>
          </Section>

          <Section id="grievance" title="14. Grievance Officer">
            <p>
              In accordance with the Information Technology Act, 2000, the Consumer
              Protection (E-Commerce) Rules, 2020 and the IT (Intermediary Guidelines
              and Digital Media Ethics Code) Rules, 2021, the details of our
              Grievance Officer are:
            </p>
            <div className="rounded-xl bg-[#F5F1EA] p-6 mt-2">
              <div className="text-sm font-semibold text-[#1C1A17]">
                Grievance Officer, Spherio Living Private Limited
              </div>
              <div className="text-sm text-[#3F3B34] mt-1">
                SCO No. 222, Shopping Square 1, Sector D, Sushant Golf City,<br />
                Lucknow 226030, Uttar Pradesh, India
              </div>
              <div className="text-sm text-[#3F3B34] mt-3">
                Email:{" "}
                <a
                  href="mailto:hello@spherioliving.com"
                  className="text-[#1C1A17] underline underline-offset-4"
                >
                  hello@spherioliving.com
                </a>
                <br />
                Phone: +91 7388047001
                <br />
                Working hours: Mon – Sat, 10 AM – 6 PM IST
              </div>
            </div>
            <p>
              We will acknowledge your complaint within 48 hours and endeavour to
              resolve it within 1 month of receipt, as required by law.
            </p>
          </Section>

          <Section id="changes" title="15. Changes to these terms">
            <p>
              We may update these Terms from time to time. The updated version will be
              posted on this page with a revised effective date. Your continued use of
              the Website or engagement of our Services after such changes constitutes
              your acceptance of the updated Terms.
            </p>
          </Section>

          <Section id="contact" title="16. Contact">
            <p>
              If you have any questions about these Terms, please contact us at{" "}
              <a
                href="mailto:hello@spherioliving.com"
                className="text-[#1C1A17] underline underline-offset-4"
              >
                hello@spherioliving.com
              </a>{" "}
              or write to Spherio Living Private Limited, SCO No. 222, Shopping Square 1, Sector D,
              Sushant Golf City, Lucknow 226030, Uttar Pradesh, India.
            </p>
          </Section>
        </div>
      </section>
    </>
  );
}
